import React, { useMemo, useRef, useEffect, useCallback, useState } from 'react';
import { MODULES, ModuleMeta } from './modulesConfig';

interface RadialNavigatorProps {
  activeId: string;
  onSelect: (id: string) => void;
  radius?: number; // base radius in px — relative to the natural (unscaled) nav size
  className?: string;
}

/*
  Accessibility & Keyboard:
  - Tab cycles through focusable nodes (standard flow)
  - ArrowRight / ArrowLeft rotate selection logically clockwise/counter-clockwise
  - Enter/Space activates navigation

  Responsive scaling:
  - The wheel renders at its natural CSS size (460px for compact+expanded, 500px otherwise).
  - A ResizeObserver on the outer wrapper detects available space and applies a
    CSS scale() transform so the wheel always fits without overflow.
*/

const RadialNavigator: React.FC<RadialNavigatorProps> = ({ activeId, onSelect, radius = 190, className }) => {
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const nodesRef = useRef<(HTMLButtonElement | null)[]>([]);
  const [scale, setScale] = useState(1);

  // natural size driven by the CSS class
  const baseNavSize = className?.includes('compact') ? 460 : 500;

  // True rendered extent: the wheel's box is `baseNavSize`, but nodes sit at
  // `radius` from center and each node adds a circle (±26px) plus a 2-line
  // label below (~28px). The bottom node therefore overhangs the nominal box,
  // so we scale against the actual content diameter to avoid clipping.
  const NODE_OVERHANG = 26 /* circle half */ + 28 /* label */;
  const contentExtent = Math.max(baseNavSize, (radius + NODE_OVERHANG) * 2);

  const ordered = useMemo(
    () => MODULES.filter((m: ModuleMeta) => m.showInRadial !== false && m.enabled !== false).sort((a: ModuleMeta, b: ModuleMeta) => a.order - b.order),
    []
  );
  const count = ordered.length;

  // Scale the fixed-pixel wheel to fit its parent container on any screen size
  useEffect(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper) return;
    const ro = new ResizeObserver(entries => {
      for (const entry of entries) {
        const { width, height } = entry.contentRect;
        const fit = Math.min(width, height);
        if (fit > 0) setScale(Math.min(1, fit / contentExtent));
      }
    });
    ro.observe(wrapper);
    return () => ro.disconnect();
  }, [contentExtent]);

  const handleKey = useCallback((e: React.KeyboardEvent) => {
    const currentIndex = ordered.findIndex((m: ModuleMeta) => m.id === activeId);
    if (currentIndex === -1) return;
    if (e.key === 'ArrowRight') {
      e.preventDefault();
      const next = (currentIndex + 1) % count;
      onSelect(ordered[next].id);
      nodesRef.current[next]?.focus();
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault();
      const prev = (currentIndex - 1 + count) % count;
      onSelect(ordered[prev].id);
      nodesRef.current[prev]?.focus();
    }
  }, [activeId, count, onSelect, ordered]);

  return (
    <div ref={wrapperRef} className="radial-nav-wrapper">
      <div
        className={`radial-nav ${className || ''}`}
        ref={containerRef}
        role="navigation"
        aria-label="System module map"
        style={{ transform: `scale(${scale})`, transformOrigin: 'center center' }}
      >
        <div className="radial-ring" aria-hidden="true" />
        {ordered.map((mod: ModuleMeta, idx: number) => {
          const angle = (idx / count) * Math.PI * 2 - Math.PI / 2; // start at top
          const x = Math.cos(angle) * radius;
          const y = Math.sin(angle) * radius;
          const isActive = mod.id === activeId;
          return (
            <button
              key={mod.id}
              ref={ref => { nodesRef.current[idx] = ref; }}
              className={`radial-node ${isActive ? 'active' : ''}`}
              data-id={mod.id}
              data-testid={`NAV_radial_node_${mod.id}`}
              style={{ transform: `translate(${x}px, ${y}px)` }}
              onClick={() => onSelect(mod.id)}
              onKeyDown={handleKey}
              aria-current={isActive ? 'page' : undefined}
              aria-label={`${mod.label}${isActive ? ' (current)' : ''}`}
            >
              <span className="radial-circle" aria-hidden>
                <span className="radial-icon">{mod.icon}</span>
              </span>
              <span className="radial-label">{mod.label}</span>
            </button>
          );
        })}
        {className?.includes('expanded') ? (
          <div className="radial-center-wrap">
            <span className="radial-center-title">GRC Hub</span>
            <span className="radial-center-sub">Click to Navigate</span>
          </div>
        ) : (
          <div className="radial-center">
            <h3>{ordered.find((m: ModuleMeta) => m.id === activeId)?.label || 'Modules'}</h3>
            <p className="center-hint">Use left/right arrows or click a node</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default RadialNavigator;
